import {
  AlertCircle,
  Check,
  ChevronDown,
  Circle,
  Copy,
  Clock3,
  Loader2,
  Mail,
  Phone,
  UserRound,
} from 'lucide-react';
import RequestStatusBadge from './RequestStatusBadge';
import { formatAmount, formatRequestDate } from './paymentRequestUtils';

function RequestStateIcon({ status }) {
  if (status === 'completed') {
    return <Check className="h-4 w-4 text-emerald-700" strokeWidth={2.5} aria-hidden="true" />;
  }
  if (status === 'expired') {
    return <Clock3 className="h-4 w-4 text-slate-500" aria-hidden="true" />;
  }
  return <AlertCircle className={`h-4 w-4 ${status === 'processing' ? 'text-cyan-700' : 'text-amber-600'}`} aria-hidden="true" />;
}

function RequestStatus({ request }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      <RequestStatusBadge status={request.status} label={`Payment: ${request.readable_status}`} />
    </span>
  );
}

function amountsDiffer(left, right) {
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber !== rightNumber;
  }
  return String(left) !== String(right);
}

function AttemptTimeline({ attempts, busyAttemptId, onCancel }) {
  if (!attempts?.length) {
    return <p className="py-3 text-sm text-slate-500">No payment attempts yet.</p>;
  }

  return (
    <div className="mt-2">
      {attempts.map((attempt, index) => (
        <div key={attempt.attempt_id} className="relative grid grid-cols-[18px_minmax(0,1fr)] gap-3 pb-3 last:pb-0 sm:grid-cols-[28px_minmax(210px,1.25fr)_minmax(120px,.7fr)_minmax(160px,.9fr)_16px]">
          {index < attempts.length - 1 ? <span className="absolute left-[8px] top-4 h-[calc(100%-4px)] w-px bg-slate-200" aria-hidden="true" /> : null}
          <span className={`relative z-10 mt-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 bg-white ${attempt.is_active ? 'border-cyan-600' : 'border-slate-300'}`}>
            {attempt.is_active ? <span className="h-1.5 w-1.5 rounded-full bg-cyan-600" /> : null}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-800">Attempt {attempt.sequence}</span>
              {attempt.is_active ? <span className="text-[10px] font-semibold uppercase text-cyan-700">Latest active</span> : null}
            </div>
            <p className="mt-0.5 truncate text-xs text-slate-500">
              {attempt.attempt_id} · {attempt.provider || 'Provider'} · {formatRequestDate(attempt.created_at, { compact: true })}
            </p>
            {attempt.terminal_reason ? <p className="mt-1 text-xs text-slate-500">{attempt.terminal_reason}</p> : null}
          </div>
          <div className="col-start-2 mt-2 flex flex-wrap items-center justify-between gap-2 sm:col-start-auto sm:mt-0 sm:contents">
            <div>
              <RequestStatusBadge status={attempt.status} label={attempt.readable_status} />
            </div>
            <div className="flex min-w-20 justify-end">
              {attempt.can_cancel ? (
                <button
                  type="button"
                  onClick={() => onCancel(attempt)}
                  disabled={busyAttemptId === attempt.attempt_id}
                  className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-wait disabled:opacity-50"
                >
                  {busyAttemptId === attempt.attempt_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Cancel
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReceiverDetails({ beneficiary = {}, requestId }) {
  const email = beneficiary.email?.trim();
  const phone = beneficiary.phone?.trim();
  const headingId = `request-${requestId}-receiver-details`;

  return (
    <section className="border-b border-slate-200 pb-4" aria-labelledby={headingId}>
      <h3 id={headingId} className="text-xs font-semibold uppercase tracking-wide text-slate-500">Receiver details</h3>
      <dl className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-cyan-50 text-cyan-700">
            <UserRound className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <dt className="text-xs font-medium text-slate-500">Receiver</dt>
            <dd className="truncate text-sm font-semibold text-slate-900">{beneficiary.full_name || 'Unknown receiver'}</dd>
          </div>
        </div>
        {email ? (
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-cyan-50 text-cyan-700">
              <Mail className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <dt className="text-xs font-medium text-slate-500">Email</dt>
              <dd className="break-all text-sm font-semibold text-slate-900">{email}</dd>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(email)}
                className="inline-flex h-9 w-9 items-center justify-center rounded text-slate-500 hover:bg-white hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600"
                aria-label="Copy receiver email address"
                title="Copy email address"
              >
                <Copy className="h-4 w-4" aria-hidden="true" />
              </button>
              <a
                href={`mailto:${email}`}
                className="inline-flex h-9 w-9 items-center justify-center rounded text-cyan-700 hover:bg-white hover:text-cyan-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600"
                aria-label="Email receiver"
                title="Email receiver"
              >
                <Mail className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        ) : null}
        {phone ? (
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-cyan-50 text-cyan-700">
              <Phone className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <dt className="text-xs font-medium text-slate-500">Phone</dt>
              <dd className="truncate text-sm font-semibold text-slate-900">{phone}</dd>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(phone)}
                className="inline-flex h-9 w-9 items-center justify-center rounded text-slate-500 hover:bg-white hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600"
                aria-label="Copy receiver phone number"
                title="Copy phone number"
              >
                <Copy className="h-4 w-4" aria-hidden="true" />
              </button>
              <a
                href={`tel:${phone}`}
                className="inline-flex h-9 w-9 items-center justify-center rounded text-cyan-700 hover:bg-white hover:text-cyan-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600"
                aria-label="Call receiver"
                title="Call receiver"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

export default function PaymentRequestCard({
  request,
  expanded,
  commandLoading,
  busyAttemptId,
  onToggle,
  onContinue,
  onCancel,
  onOpenDetails,
}) {
  const attemptCount = request.attempts?.length || 0;
  const requestedAmount = request.amount?.value;
  const confirmedAmount = request.amount?.confirmed_value;
  const displayAmount = confirmedAmount ?? requestedAmount;
  const showRequestedAmount = confirmedAmount != null && amountsDiffer(confirmedAmount, requestedAmount);
  const asset = request.amount?.asset || 'USDC';
  const isNotStarted = request.status === 'not_started';

  return (
    <article className={`overflow-hidden rounded-md border bg-white transition-colors duration-300 motion-reduce:transition-none ${expanded ? 'border-cyan-300 shadow-[0_0_0_1px_rgba(8,145,178,.05)]' : 'border-slate-200 hover:border-slate-300'}`}>
      <button
        type="button"
        onClick={onToggle}
        className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 px-4 py-3.5 text-left sm:grid-cols-[28px_minmax(210px,1.25fr)_minmax(120px,.7fr)_minmax(160px,.9fr)_16px] sm:items-center"
        aria-expanded={expanded}
        aria-controls={`request-${request.request_id}-details`}
      >
        <span className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-full ${request.status === 'completed' ? 'bg-emerald-50' : request.status === 'processing' ? 'bg-cyan-50' : 'bg-amber-50'}`}>
          <RequestStateIcon status={request.status} />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-slate-900">{request.beneficiary?.full_name || 'Unknown receiver'}</span>
          <span className="mt-1 block truncate font-mono text-[10px] text-slate-500">Request · {request.request_id}</span>
        </span>
        <span className="hidden sm:block"><RequestStatus request={request} /></span>
        <span className="col-start-2 row-start-2 min-w-0 sm:col-start-4 sm:row-start-1 sm:text-right">
          <span className="block text-sm font-semibold tabular-nums text-slate-900">
            {formatAmount(displayAmount)} {asset}
          </span>
          <span className="mt-1 block text-xs text-slate-500">
            {showRequestedAmount ? `Requested ${formatAmount(requestedAmount)} ${asset} · ` : null}
            Created {formatRequestDate(request.created_at)}
            {!isNotStarted ? ` · ${attemptCount} ${attemptCount === 1 ? 'attempt' : 'attempts'}` : null}
          </span>
        </span>
        <ChevronDown
          className={`col-start-3 row-start-1 h-4 w-4 text-slate-500 transition-transform duration-300 ease-out motion-reduce:transition-none sm:col-start-5 ${expanded ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
        <span className="col-start-2 row-start-3 mt-1 sm:hidden"><RequestStatus request={request} /></span>
      </button>

      <div
        id={`request-${request.request_id}-details`}
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none ${expanded ? 'grid-rows-[1fr] opacity-100' : 'pointer-events-none grid-rows-[0fr] opacity-0'}`}
        aria-hidden={!expanded}
        inert={expanded ? undefined : ''}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="border-t border-slate-200 bg-slate-50/50 px-4 py-4">
          <ReceiverDetails beneficiary={request.beneficiary} requestId={request.request_id} />

          <div className={`mt-4 flex flex-col gap-3 sm:flex-row sm:items-center ${isNotStarted ? 'sm:justify-end' : 'sm:justify-between'}`}>
            {!isNotStarted ? (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payment attempts</h3>
                <p className="mt-1 text-xs text-slate-500">{attemptCount} {attemptCount === 1 ? 'attempt' : 'attempts'} · newest first</p>
              </div>
            ) : null}
            {request.available_actions?.continue ? (
              <button
                type="button"
                onClick={() => onContinue(request)}
                disabled={commandLoading}
                className="inline-flex h-9 items-center justify-center gap-2 self-start rounded bg-cyan-700 px-4 text-sm font-semibold text-white hover:bg-cyan-800 disabled:cursor-wait disabled:opacity-60 sm:self-auto"
              >
                {commandLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                {isNotStarted ? 'Review and continue' : 'Continue payment'}
              </button>
            ) : null}
          </div>

          {!isNotStarted ? (
            <AttemptTimeline attempts={request.attempts} busyAttemptId={busyAttemptId} onCancel={onCancel} />
          ) : null}

          {!isNotStarted ? (
            <div className="mt-3 flex flex-col gap-3 border-t border-slate-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex min-w-0 items-center gap-2 text-xs text-slate-600">
                <Circle className={`h-3.5 w-3.5 shrink-0 ${request.payout_status === 'completed' ? 'fill-emerald-500 text-emerald-500' : 'text-slate-400'}`} aria-hidden="true" />
                <span><span className="font-semibold text-slate-700">Transfer outcome:</span> {request.transfer_outcome}</span>
              </p>
              {onOpenDetails ? (
                <button type="button" onClick={onOpenDetails} className="self-start text-xs font-semibold text-cyan-700 hover:text-cyan-900 sm:self-auto">View request details</button>
              ) : null}
            </div>
          ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}
